// The Briary Hub component kit. Pages build from these so everything looks and behaves the same.
import { useEffect, useId, useLayoutEffect, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react'
import { Icon, type IconName } from './icons'
import { DAY_MONTH, ordinalDay } from '@briary/shared'

type ButtonVariant = 'default' | 'primary' | 'danger' | 'ghost'

export function Button({
  variant = 'default',
  size,
  block,
  busy,
  icon,
  children,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: 'sm' | 'lg'; block?: boolean; busy?: boolean; icon?: IconName }) {
  const cls = ['btn', variant !== 'default' && `btn-${variant}`, size && `btn-${size}`, block && 'btn-block', className].filter(Boolean).join(' ')
  return (
    <button type="button" className={cls} disabled={busy || rest.disabled} {...rest}>
      {busy ? <span className="spinner" /> : icon ? <Icon name={icon} width={16} height={16} /> : null}
      {children}
    </button>
  )
}

export function Field({ label, hint, error, children }: { label: string; hint?: ReactNode; error?: string; children: (id: string) => ReactNode }) {
  const id = useId()
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      {children(id)}
      {error ? <div className="field-error">{error}</div> : hint ? <div className="field-hint">{hint}</div> : null}
    </div>
  )
}

export function TextField({
  label,
  hint,
  error,
  className = '',
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: ReactNode; error?: string }) {
  return (
    <Field label={label} hint={hint} error={error}>
      {(id) => <input id={id} className={`input ${className}`} aria-invalid={error ? true : undefined} {...rest} />}
    </Field>
  )
}

export function Card({ title, actions, children, pad = true }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; pad?: boolean }) {
  return (
    <section className="card">
      {(title || actions) && (
        <div className="card-head">
          {typeof title === 'string' ? <h3>{title}</h3> : title}
          {actions && <div className="row">{actions}</div>}
        </div>
      )}
      <div className={pad ? 'card-body' : undefined}>{children}</div>
    </section>
  )
}

export function PageHeader({ title, kicker, description, actions }: { title: string; kicker?: string; description?: ReactNode; actions?: ReactNode }) {
  useEffect(() => {
    document.title = `${title} · Briary Hub`
  }, [title])
  return (
    <header className="page-head">
      <div>
        {kicker && <div className="caps crumbs">{kicker}</div>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="row">{actions}</div>}
    </header>
  )
}

export function Stat({ label, value, note }: { label: string; value: ReactNode; note?: ReactNode }) {
  // A long figure (£12,927,330.15) shrinks to fit its tile instead of wrapping mid-number.
  const ref = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const fit = () => {
      el.style.fontSize = ''
      for (let size = 40; size > 18 && el.scrollWidth > el.clientWidth; size -= 2) el.style.fontSize = `${size - 2}px`
    }
    fit()
    const watch = new ResizeObserver(fit)
    watch.observe(el)
    return () => watch.disconnect()
  })
  return (
    <div className="card stat">
      <div className="caps">{label}</div>
      <div className="stat-value" ref={ref}>{value}</div>
      {note && <div className="stat-note">{note}</div>}
    </div>
  )
}

export function Notice({ tone = 'info', children }: { tone?: 'info' | 'good' | 'warn' | 'bad'; children: ReactNode }) {
  const icon: IconName = tone === 'good' ? 'check' : tone === 'info' ? 'info' : 'alert'
  return (
    <div className={`notice notice-${tone}`} role={tone === 'bad' ? 'alert' : 'status'}>
      <Icon name={icon} width={16} height={16} style={{ flex: 'none', marginTop: 2 }} />
      <div>{children}</div>
    </div>
  )
}

export function Badge({ tone, children }: { tone?: 'good' | 'warn' | 'bad' | 'brand'; children: ReactNode }) {
  return <span className={`badge${tone ? ` badge-${tone}` : ''}`}>{children}</span>
}

export function Empty({ title, children, icon = 'folder' }: { title: string; children?: ReactNode; icon?: IconName }) {
  return (
    <div className="empty">
      <Icon name={icon} width={32} height={32} style={{ marginBottom: 10, opacity: 0.6 }} />
      <h3>{title}</h3>
      {children && <div>{children}</div>}
    </div>
  )
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="center-fill">
      <div className="row">
        <span className="spinner" /> {label}
      </div>
    </div>
  )
}

export function Modal({ onClose, children, className = '', labelledBy, describedBy }: { onClose: () => void; children: ReactNode; className?: string; labelledBy?: string; describedBy?: string }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${className}`} role="dialog" aria-modal="true" aria-labelledby={labelledBy} aria-describedby={describedBy}>
        {children}
      </div>
    </div>
  )
}

export const formatDateTime = (v: string | Date | null | undefined) =>
  v ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(DAY_MONTH, ordinalDay) : '—'

export const timeAgo = (v: string | Date | null | undefined) => {
  if (!v) return 'never'
  const s = (Date.now() - new Date(v).getTime()) / 1000
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)} min ago`
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`
  if (s < 86400 * 30) return `${Math.floor(s / 86400)} days ago`
  return new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).replace(DAY_MONTH, ordinalDay)
}

export { Icon }
export { VirtualTable, type VtColumn } from './VirtualTable'
